import React, {useCallback, useEffect, useRef, useState} from "react";
import {css, cva, cx} from "@panda/css";

import {Text} from "@pg-design/text-module";

import {InquiryButtonTypeName} from "../../../../constants/inquiry_button_type";
import {prettyScroll} from "../../../../styles/receipes/pretty_scroll";
import {GTMEventApplicationSourceSection} from "../../../application/types/lead/GTMEvenApplicationSourceSection";
import {FavouritesApplication} from "../../../favorite/components/FavouritesApplication/FavouritesApplication";
import {useMyOffersFavourites} from "../../../my_offers/hooks/use_my_offers_favourites";
import {PropertyListItem} from "../../../property/components/PropertyListItem";
import {isPropertyArchived} from "../../../property/utils/is_property_archived";
import {gtmEventFormOpen} from "../../../tracking/google_tag_manager/gtm_event_form_open";
import {IAskForAllModalViewData} from "./ask_for_all_modal_types";

interface IProps {
    setModalView: (viewData: IAskForAllModalViewData) => void;
}

export const AskForAllModalListWithForm = (props: IProps) => {
    const {setModalView} = props;
    const {favouriteProperties} = useMyOffersFavourites();
    const [hasFog, setHasFog] = useState(false);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const activeProperties = favouriteProperties.filter((property) => !isPropertyArchived(property));

    const handleFogVisibility = useCallback(() => {
        const scrollContainer = scrollContainerRef.current;

        if (scrollContainer) {
            const scrolledToBottom = Math.ceil(scrollContainer.scrollTop + scrollContainer.clientHeight) >= scrollContainer.scrollHeight;

            if (scrolledToBottom) {
                setHasFog(false);
            }

            if (!scrolledToBottom) {
                setHasFog(true);
            }
        }
    }, []);

    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        gtmEventFormOpen(InquiryButtonTypeName.ASK_FOR_ALL, GTMEventApplicationSourceSection.COMPARISON_TOOL, "", "");

        if (scrollContainer) {
            const hasScroll = scrollContainer.scrollHeight > scrollContainer.clientHeight;

            if (hasScroll) {
                setHasFog(true);
            }

            scrollContainer.addEventListener("scroll", handleFogVisibility);

            return () => {
                scrollContainer.removeEventListener("scroll", handleFogVisibility);
            };
        }
    }, []);

    return (
        <div className={modalContentWrapStyle}>
            <div className={applicationWrapStyle}>
                <Text variant="headline_4" as="h4" className={mobileFormTitleStyle}>
                    Zapytaj o porównywane oferty
                </Text>
                <FavouritesApplication
                    onSubmitSuccess={(_, sentOffers, rejectedOffers, sentProperties, rejectedProperties) => {
                        if (rejectedOffers.length === 0 && rejectedProperties.length === 0) {
                            setModalView({
                                type: "justConfirm"
                            });
                        }

                        if (rejectedOffers.length > 0 || rejectedProperties.length > 0) {
                            setModalView({
                                type: "informAboutRejected",
                                sentOffers,
                                rejectedOffers,
                                sentProperties,
                                rejectedProperties
                            });
                        }
                    }}
                />
            </div>
            <div className={listWithFogStyle}>
                <Text variant="headline_4" as="h4" className={desktopFormTitleStyle}>
                    Zapytaj o porównywane oferty
                </Text>
                <div className={listWrapStyle({hasFog})}>
                    <div className={cx(scrollWrapStyle, css(prettyScroll))} ref={scrollContainerRef}>
                        <div className={listItemsStyle}>
                            {activeProperties.map((property) => (
                                <PropertyListItem
                                    key={`afamp${property.id}`}
                                    id={property.id}
                                    offerType={property.offer.type}
                                    offerName={property.offer.name}
                                    offerVendorName={property.offer.vendor.name}
                                    offerAddress={property.offer.address}
                                    offerPrivacyPolicyUrl={property.offer.configuration.privacy_policy_url}
                                    offerPrivacyDataProcessorName={property.offer.configuration.personal_data_processor_name}
                                    offerPersonalDataProcessorUrl={property.offer.configuration.personal_data_processor_url}
                                    rooms={property.rooms}
                                    area={property.area}
                                    imageSrc={property.offer.main_image.m_img_350x230}
                                    constructionDateRange={property.offer.construction_date_range}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

/*
    Styles
 */
const modalContentWrapStyle = css({
    display: "flex",
    flexDirection: "column",
    sm: {
        flexDirection: "row"
    }
});

const applicationWrapStyle = css({
    order: 0,
    backgroundColor: "var(--colors-background-100)",
    py: 2,
    px: 1.5,
    pb: 4,
    sm: {
        flex: "1 0 374px",
        order: 1,
        overflow: "hidden",
        pt: "9.6rem",
        px: 2,
        pb: 4
    }
});

const mobileFormTitleStyle = css({
    py: 0,
    pr: 10,
    pl: 1,
    mb: 3,
    sm: {
        display: "none"
    }
});

const desktopFormTitleStyle = css({
    display: "none",
    sm: {
        display: "block",
        mb: 5
    }
});

const listWithFogStyle = css({
    order: 1,
    display: "flex",
    flexDirection: "column",
    py: 2,
    px: 1.5,
    pb: 4,
    position: "relative",
    sm: {
        flex: "1 1 auto",
        order: 0,
        pt: 3,
        pr: 2,
        pb: 3,
        pl: 5
    }
});

const listWrapStyle = cva({
    base: {
        position: "relative",
        _after: {
            content: '""',
            height: "70px",
            position: "absolute",
            bottom: 0,
            left: 0,
            background: "linear-gradient(360deg, #ffffff 0%, rgba(255, 255, 255, 0) 100%)",
            width: "100%",
            zIndex: 5
        }
    },
    variants: {
        hasFog: {
            true: {
                _after: {
                    display: "block"
                }
            },
            false: {
                _after: {
                    display: "none"
                }
            }
        }
    }
});

const scrollWrapStyle = css({
    sm: {
        maxHeight: "460px"
    },
    md: {
        maxHeight: "490px"
    }
});

const listItemsStyle = css({
    display: "flex",
    flexDirection: "column",
    rowGap: 1.5,
    sm: {
        rowGap: 2,
        pr: 2
    }
});
import React, {useCallback, useEffect, useRef, useState} from "react";
import {css, cva, cx} from "@panda/css";

import {Text} from "@pg-design/text-module";

import {InquiryButtonTypeName} from "../../../../constants/inquiry_button_type";
import {prettyScroll} from "../../../../styles/receipes/pretty_scroll";
import {GTMEventApplicationSourceSection} from "../../../application/types/lead/GTMEvenApplicationSourceSection";
import {FavouritesApplication} from "../../../favorite/components/FavouritesApplication/FavouritesApplication";
import {useMyOffersFavourites} from "../../../my_offers/hooks/use_my_offers_favourites";
import {PropertyListItem} from "../../../property/components/PropertyListItem";
import {isPropertyArchived} from "../../../property/utils/is_property_archived";
import {gtmEventFormOpen} from "../../../tracking/google_tag_manager/gtm_event_form_open";
import {IAskForAllModalViewData} from "./ask_for_all_modal_types";

interface IProps {
    setModalView: (viewData: IAskForAllModalViewData) => void;
}

export const AskForAllModalListWithForm = (props: IProps) => {
    const {setModalView} = props;
    const {favouriteProperties} = useMyOffersFavourites();
    const [hasFog, setHasFog] = useState(false);
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const activeProperties = favouriteProperties.filter((property) => !isPropertyArchived(property));

    const handleFogVisibility = useCallback(() => {
        const scrollContainer = scrollContainerRef.current;

        if (scrollContainer) {
            const scrolledToBottom = Math.ceil(scrollContainer.scrollTop + scrollContainer.clientHeight) >= scrollContainer.scrollHeight;

            if (scrolledToBottom) {
                setHasFog(false);
            }

            if (!scrolledToBottom) {
                setHasFog(true);
            }
        }
    }, []);

    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;
        gtmEventFormOpen(InquiryButtonTypeName.ASK_FOR_ALL, GTMEventApplicationSourceSection.COMPARISON_TOOL, "", "");

        if (scrollContainer) {
            const hasScroll = scrollContainer.scrollHeight > scrollContainer.clientHeight;

            if (hasScroll) {
                setHasFog(true);
            }

            scrollContainer.addEventListener("scroll", handleFogVisibility);

            return () => {
                scrollContainer.removeEventListener("scroll", handleFogVisibility);
            };
        }
    }, []);

    return (
        <div className={modalContentWrapStyle}>
            <div className={applicationWrapStyle}>
                <Text variant="headline_4" as="h4" className={mobileFormTitleStyle}>
                    Zapytaj o porównywane oferty
                </Text>
                <FavouritesApplication
                    onSubmitSuccess={(_, sentOffers, rejectedOffers, sentProperties, rejectedProperties) => {
                        if (rejectedOffers.length === 0 && rejectedProperties.length === 0) {
                            setModalView({
                                type: "justConfirm"
                            });
                        }

                        if (rejectedOffers.length > 0 || rejectedProperties.length > 0) {
                            setModalView({
                                type: "informAboutRejected",
                                sentOffers,
                                rejectedOffers,
                                sentProperties,
                                rejectedProperties
                            });
                        }
                    }}
                />
            </div>
            <div className={listWithFogStyle}>
                <Text variant="headline_4" as="h4" className={desktopFormTitleStyle}>
                    Zapytaj o porównywane oferty
                </Text>
                <div className={listWrapStyle({hasFog})}>
                    <div className={cx(scrollWrapStyle, css(prettyScroll))} ref={scrollContainerRef}>
                        <div className={listItemsStyle}>
                            {activeProperties.map((property) => (
                                <PropertyListItem
                                    key={`afamp${property.id}`}
                                    id={property.id}
                                    offerType={property.offer.type}
                                    offerName={property.offer.name}
                                    offerVendorName={property.offer.vendor.name}
                                    offerAddress={property.offer.address}
                                    offerPrivacyPolicyUrl={property.offer.configuration.privacy_policy_url}
                                    offerPrivacyDataProcessorName={property.offer.configuration.personal_data_processor_name}
                                    offerPersonalDataProcessorUrl={property.offer.configuration.personal_data_processor_url}
                                    rooms={property.rooms}
                                    area={property.area}
                                    imageSrc={property.offer.main_image.m_img_350x230}
                                    constructionDateRange={property.offer.construction_date_range}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

/*
    Styles
 */
const modalContentWrapStyle = css({
    display: "flex",
    flexDirection: "column",
    sm: {
        flexDirection: "row"
    }
});

const applicationWrapStyle = css({
    order: 0,
    backgroundColor: "var(--colors-background-100)",
    py: 2,
    px: 1.5,
    pb: 4,
    sm: {
        flex: "1 0 374px",
        order: 1,
        overflow: "hidden",
        pt: "9.6rem",
        px: 2,
        pb: 4
    }
});

const mobileFormTitleStyle = css({
    py: 0,
    pr: 10,
    pl: 1,
    mb: 3,
    sm: {
        display: "none"
    }
});

const desktopFormTitleStyle = css({
    display: "none",
    sm: {
        display: "block",
        mb: 5
    }
});

const listWithFogStyle = css({
    order: 1,
    display: "flex",
    flexDirection: "column",
    py: 2,
    px: 1.5,
    pb: 4,
    position: "relative",
    sm: {
        flex: "1 1 auto",
        order: 0,
        pt: 3,
        pr: 2,
        pb: 3,
        pl: 5
    }
});

const listWrapStyle = cva({
    base: {
        position: "relative",
        _after: {
            content: '""',
            height: "70px",
            position: "absolute",
            bottom: 0,
            left: 0,
            background: "linear-gradient(360deg, #ffffff 0%, rgba(255, 255, 255, 0) 100%)",
            width: "100%",
            zIndex: 5
        }
    },
    variants: {
        hasFog: {
            true: {
                _after: {
                    display: "block"
                }
            },
            false: {
                _after: {
                    display: "none"
                }
            }
        }
    }
});

const scrollWrapStyle = css({
    sm: {
        maxHeight: "460px"
    },
    md: {
        maxHeight: "490px"
    }
});

const listItemsStyle = css({
    display: "flex",
    flexDirection: "column",
    rowGap: 1.5,
    sm: {
        rowGap: 2,
        pr: 2
    }
});
